import { css } from '@emotion/core';
import styled from '@emotion/styled';
import type { StepData, Option } from '@kizen/filters/types';

import { PropsWithChildren, useRef, useState } from 'react';
import Overlay from 'react-bootstrap/Overlay';
import { KizenTypography } from '__app/typography';
import { InputContainer } from './inputs/styled';
import { SelectOption } from './inputs/types';
import { DeleteRowButton } from './components';
import { ErrorCard, ErrorCardWrapper } from './styled';
import useDebounce from 'react-use/lib/useDebounce';
import { GroupedDropdownStatic } from './inputs/GroupedDropdownStatic';
import type { DropdownProps } from './inputs/types';

type FilterTypeSelectorProps = {
  value?: SelectOption;
  placeholder?: string;
  options: Option[];
  option_paths: StepData['option_paths'];
  option_groups: StepData['option_groups'];
  option_filter: StepData['option_filter'];
  value_id_path: StepData['value_id_path'];
  width: number;
  error?: string | false;
  onChange: DropdownProps['onChange'];
  translateY?: number | null;
  modalLayer?: number;
  hideOutOfBoundaries?: boolean;
  disabled?: boolean;
};

type FilterTypeSelectorContainerProps = {
  onDelete?(): void;
  showDeleteIcon?: boolean;
};

const Container = styled.div<FilterTypeSelectorContainerProps>`
  display: flex;
  gap: 15px;
  align-items: center;

  ${({ showDeleteIcon = false }) =>
    showDeleteIcon &&
    css`
      margin-left: -28px;
    `}
`;

export const FilterTypeSelectorContainer = ({
  showDeleteIcon,
  children,
  onDelete,
}: PropsWithChildren<FilterTypeSelectorContainerProps>) => (
  <Container showDeleteIcon={showDeleteIcon}>
    {showDeleteIcon && <DeleteRowButton onClick={onDelete} />}
    {children}
  </Container>
);

export const FilterTypeSelector = ({
  value,
  placeholder,
  width,
  options,
  option_filter,
  option_groups,
  value_id_path,
  option_paths,
  error,
  translateY = null,
  hideOutOfBoundaries = false,
  modalLayer,
  onChange,
  disabled = false,
}: FilterTypeSelectorProps) => {
  // wait for the error to be set before showing it to keep the overlay in the correct place
  const [debouncedError, setDebouncedError] = useState<any>(null);
  useDebounce(() => setDebouncedError(error), 350, [error]);
  const containerRef = useRef<any>();
  const showError = Boolean(debouncedError);

  return (
    <InputContainer
      ref={containerRef}
      data-qa="filter-type-selector"
      width={width}
    >
      <GroupedDropdownStatic
        step="filter_type"
        value={value}
        options={options}
        width={width}
        option_groups={option_groups}
        option_paths={option_paths}
        option_filter={option_filter}
        value_id_path={value_id_path}
        placeholder={placeholder}
        error={debouncedError}
        onChange={onChange}
        disabled={disabled}
      />
      {/* @ts-expect-error - The Overlay types are wrong */}
      <Overlay
        placement="bottom-start"
        transition={false}
        show={showError}
        target={containerRef.current}
      >
        <ErrorCardWrapper
          modalLayer={modalLayer}
          hideOutOfBoundaries={hideOutOfBoundaries}
        >
          <ErrorCard show={showError} duration="300ms" translateY={translateY}>
            <KizenTypography>{debouncedError}</KizenTypography>
          </ErrorCard>
        </ErrorCardWrapper>
      </Overlay>
    </InputContainer>
  );
};
